import { useState, useEffect, useRef, useCallback } from 'react'
import { fmtCoins } from '../../lib/cards'
import { io } from 'socket.io-client'
import { PlaneCanvas } from './PlaneCanvas'
import { Sounds } from '../../lib/sounds'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'

interface AviatorState {
  status:       'idle' | 'betting' | 'flying' | 'crashed'
  multiplier:   number
  crashPoint:   number | null
  roundId:      string | null
  bettingCloses: number
  seedHash:     string | null
}

interface MyBet {
  stake:       number
  autoCashout: number | null
  cashedOut:   boolean
  multiplier:  number | null
  payout:      number | null
}

interface CrashHistory {
  multiplier: number
  roundId:    string
}

interface Props {
  token:       string
  balance:     number | null
  displayName: string
  operatorId:  string
  playerId:    string
}

const CHIP_VALS   = [5, 10, 25, 50, 100, 500]
const CHIP_COLORS = ['#dc2626','#2563eb','#7c3aed','#d97706','#059669','#db2777']

function multiplierColor(m: number): string {
  if (m < 2)  return '#22c55e'
  if (m < 5)  return '#3b82f6'
  if (m < 10) return '#8b5cf6'
  return '#f97316'
}

function PlaneIcon({ crashed, flying }: { crashed: boolean; flying: boolean }) {
  return (
    <svg width="60" height="40" viewBox="0 0 60 40" style={{
      filter: crashed ? 'drop-shadow(0 0 8px #ef4444)' : flying ? 'drop-shadow(0 0 12px #22c55e)' : 'none',
      transition: 'filter 0.3s',
    }}>
      <path d="M5 20 L40 8 L55 20 L40 24 Z" fill={crashed ? '#ef4444' : '#e2e8f0'} />
      <path d="M20 20 L30 28 L35 24 L25 18 Z" fill={crashed ? '#dc2626' : '#94a3b8'} />
      <path d="M10 20 L18 24 L20 20 Z" fill={crashed ? '#dc2626' : '#64748b'} />
      <circle cx="45" cy="18" r="3" fill={crashed ? '#ef4444' : '#3b82f6'} />
    </svg>
  )
}

export function AviatorGame({ token, balance, displayName, operatorId, playerId }: Props) {
  
  const socketRef = useRef<any>(null)
  const [socket, setSocket] = useState<any>(null)

  useEffect(() => {
    const GAME_SERVER = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3002'
    const s = io(GAME_SERVER, {
      transports:           ['polling'],
      auth:                 { token },
      reconnectionAttempts: 20,
      reconnectionDelay:    500,
      timeout:              20000,
    })
    socketRef.current = s
    setSocket(s)
    return () => { s.disconnect() }
  }, [token])

  const [state,       setState]       = useState<AviatorState>({ status: 'idle', multiplier: 1.00, crashPoint: null, roundId: null, bettingCloses: 0, seedHash: null })
  const [myBet,       setMyBet]       = useState<MyBet | null>(null)
  const [stake,       setStake]       = useState(10)
  const [selectedChip,setSelectedChip]= useState(10)
  const [autoCashout, setAutoCashout] = useState<string>('')
  const [history,     setHistory]     = useState<CrashHistory[]>([])
  const [toast,       setToast]       = useState<string | null>(null)

  const [localBalance, setLocalBalance] = useState<number | null>(balance)

  useEffect(() => {
    setLocalBalance(balance)
  }, [balance])

  const [planeX,      setPlaneX]      = useState(10)
  const [planeY,      setPlaneY]      = useState(70)
  const [trail,       setTrail]       = useState<{ x: number; y: number }[]>([])
  const animRef = useRef<number>()
  const startTimeRef = useRef<number>(0)
  const crashedRef = useRef(false)

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  // Animate plane
  useEffect(() => {
    if (state.status === 'flying') {
      crashedRef.current = false
      startTimeRef.current = Date.now()
      const animate = () => {
        if (crashedRef.current) return
        const elapsed = (Date.now() - startTimeRef.current) / 1000
        const progress = Math.min(elapsed / 30, 1)
        const x = 10 + progress * 75
        const y = 70 - progress * 60
        setPlaneX(x)
        setPlaneY(y)
        setTrail(prev => [...prev.slice(-40), { x, y }])
        animRef.current = requestAnimationFrame(animate)
      }
      animRef.current = requestAnimationFrame(animate)
    } else if (state.status === 'crashed') {
      crashedRef.current = true
      if (animRef.current) cancelAnimationFrame(animRef.current)
    } else if (state.status === 'betting' || state.status === 'idle') {
      setPlaneX(10)
      setPlaneY(70)
      setTrail([])
      crashedRef.current = false
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [state.status])

  // Socket events
  useEffect(() => {
    if (!socket) return

    socket.onAny((event: string, ...args: any[]) => {
      if (event.startsWith('aviator')) console.log('[AVIATOR]', event, args)
    })

    socket.on('aviator:betting', (data: any) => {
      setState({ status: 'betting', multiplier: 1.00, crashPoint: null, roundId: data.round_id, bettingCloses: data.betting_closes, seedHash: data.seed_hash })
      setMyBet(null)
      Sounds.aviatorBetting()
    })

    socket.on('aviator:flying', (data: any) => {
      setState(prev => ({ ...prev, status: 'flying', roundId: data.round_id }))
      Sounds.aviatorTakeoff()
    })

    socket.on('aviator:tick', (data: any) => {
      setState(prev => ({
        ...prev,
        multiplier: data.multiplier,
        status: prev.status === 'betting' || prev.status === 'idle' ? 'flying' : prev.status,
        roundId: data.round_id,
      }))
      Sounds.aviatorTick(data.multiplier)
    })

    socket.on('aviator:crashed', (data: any) => {
      setState(prev => ({ ...prev, status: 'crashed', crashPoint: data.multiplier, multiplier: data.multiplier }))
      Sounds.aviatorCrash()
      setHistory(prev => [{ multiplier: data.multiplier, roundId: data.round_id }, ...prev].slice(0, 20))
      // If player didn't cash out, mark as lost
      setMyBet(prev => prev && !prev.cashedOut ? { ...prev, cashedOut: true, payout: 0 } : prev)
    })

    socket.on('aviator:cashout_confirmed', (data: any) => {
      Sounds.aviatorCashout()
      setMyBet(prev => prev ? { ...prev, cashedOut: true, multiplier: data.multiplier, payout: data.payout } : prev)
      setLocalBalance(prev => prev !== null ? prev + data.payout : prev)
      showToast(`✓ Cashed out at ${data.multiplier}x — +${fmtCoins(data.payout)} coins!`)
    })

    socket.on('aviator:bet_confirmed', (data: any) => {
      if (data.success) {
        setLocalBalance(prev => prev !== null ? prev - data.stake : prev)
        showToast(`Bet placed: ${fmtCoins(data.stake)} coins`)
      }
    })

    // Connect AFTER listeners are registered, and request state on every connect
    const requestState = () => {
      socket.emit('aviator:request_state', { operator_id: operatorId })
    }
    socket.on('connect', requestState)
    // Request immediately if already connected
    if (socket.connected) requestState()

    return () => {
      socket.off('connect', requestState)
      socket.off('aviator:betting')
      socket.off('aviator:flying')
      socket.off('aviator:tick')
      socket.off('aviator:crashed')
      socket.off('aviator:cashout_confirmed')
      socket.off('aviator:bet_confirmed')
    }
  }, [socket])

  const placeBet = useCallback(() => {
    if (!socket || state.status !== 'betting' || myBet) return
    const ac = autoCashout ? parseFloat(autoCashout) : null
    socket.emit('aviator:bet', {
      player_id:    playerId,
      operator_id:  operatorId,
      stake,
      auto_cashout: ac && ac > 1 ? ac : null,
    })
    setMyBet({ stake, autoCashout: ac, cashedOut: false, multiplier: null, payout: null })
  }, [socket, state.status, myBet, stake, autoCashout, playerId, operatorId])

  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])
  const myBetRef = useRef(myBet)
  useEffect(() => { myBetRef.current = myBet }, [myBet])

  const cashOut = useCallback(() => {
    const currentState = stateRef.current
    const currentBet   = myBetRef.current
    
    if (!socket) { console.log('no socket'); return }
    if (currentState.status !== 'flying') { console.log('not flying:', currentState.status); return }
    if (!currentBet || currentBet.cashedOut) { console.log('no bet or already cashed'); return }
    
    socket.emit('aviator:cashout', { player_id: playerId, operator_id: operatorId })
    
  }, [socket, playerId, operatorId])

  const isBetting = state.status === 'betting'
  const isFlying  = state.status === 'flying'
  const isCrashed = state.status === 'crashed'
  const canCashOut = isFlying && myBet && !myBet.cashedOut
  const currentMultiplier = state.multiplier

  // Countdown
  const [countdown, setCountdown] = useState(0)
  useEffect(() => {
    if (!isBetting) return
    const update = () => setCountdown(Math.max(0, Math.ceil((state.bettingCloses - Date.now()) / 1000)))
    update()
    const iv = setInterval(update, 200)
    return () => clearInterval(iv)
  }, [isBetting, state.bettingCloses])

  // Build trail SVG path
  const trailPath = trail.length > 1
    ? `M ${trail[0].x} ${trail[0].y} ` + trail.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')
    : ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0f1117', color: '#e2e8f0', fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 13 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', background: 'linear-gradient(180deg, #0a0d14 0%, #131720 100%)', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>✈</span>
          <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: 1, background: 'linear-gradient(90deg, #22c55e, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AVIATOR</span>
          <span style={{ fontSize: 10, color: '#64748b', padding: '2px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: 20 }}>Provably Fair</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {state.roundId && <span style={{ fontSize: 10, color: '#334155' }}>{state.roundId.slice(0,12).toUpperCase()}</span>}
          <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, padding: '4px 12px', fontWeight: 700, color: '#c9a84c' }}>
            BAL {localBalance !== null ? `₹${fmtCoins(localBalance)}` : '—'}
          </div>
          <SoundToggleBtn />
        </div>
      </div>

      {/* Crash history */}
      <div style={{ display: 'flex', gap: 6, padding: '6px 14px', background: '#0a0d14', borderBottom: '1px solid rgba(255,255,255,0.04)', flexShrink: 0, overflowX: 'auto' }}>
        {history.length === 0 && <span style={{ fontSize: 10, color: '#334155' }}>No rounds yet</span>}
        {history.map((h, i) => (
          <div key={i} style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, flexShrink: 0,
            background: h.multiplier < 2 ? 'rgba(239,68,68,0.15)' : h.multiplier < 5 ? 'rgba(59,130,246,0.15)' : 'rgba(139,92,246,0.15)',
            color: h.multiplier < 2 ? '#f87171' : h.multiplier < 5 ? '#60a5fa' : '#a78bfa',
            border: `1px solid ${h.multiplier < 2 ? 'rgba(239,68,68,0.3)' : h.multiplier < 5 ? 'rgba(59,130,246,0.3)' : 'rgba(139,92,246,0.3)'}`,
          }}>
            {h.multiplier.toFixed(2)}x
          </div>
        ))}
      </div>

      {/* Main sky area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#0a0d14' }}>
        <PlaneCanvas
          phase={state.status}
          multiplier={state.multiplier}
          countdown={countdown}
        />

        {/* Stars */}
        {[...Array(30)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${(i * 37) % 100}%`,
            top: `${(i * 23) % 60}%`,
            width: i % 3 === 0 ? 2 : 1,
            height: i % 3 === 0 ? 2 : 1,
            borderRadius: '50%',
            background: 'white',
            opacity: 0.2 + (i % 5) * 0.1,
          }} />
        ))}

        {/* Grid lines */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06 }} preserveAspectRatio="none">
          {[10,20,30,40,50,60,70,80,90].map(x => (
            <line key={x} x1={`${x}%`} y1="0" x2={`${x}%`} y2="100%" stroke="#22c55e" strokeWidth="0.5" />
          ))}
          {[10,20,30,40,50,60,70,80,90].map(y => (
            <line key={y} x1="0" y1={`${y}%`} x2="100%" y2={`${y}%`} stroke="#22c55e" strokeWidth="0.5" />
          ))}
        </svg>

        {/* Trail */}
        {trail.length > 1 && (
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d={trailPath} fill="none" stroke={isCrashed ? '#ef4444' : '#22c55e'} strokeWidth="0.4" opacity="0.6" />
          </svg>
        )}

        {/* Plane */}
        {state.status !== 'idle' && (
          <div style={{
            position: 'absolute',
            left: `${planeX}%`,
            top: `${planeY}%`,
            transform: 'translate(-50%, -50%)',
            transition: isCrashed ? 'none' : undefined,
            animation: isCrashed ? 'planeCrash 0.5s ease-out forwards' : undefined,
          }}>
            <PlaneIcon crashed={isCrashed} flying={isFlying} />
          </div>
        )}

        {/* Multiplier display */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
          {state.status === 'betting' && (
            <div style={{ animation: 'fadeInUp 0.3s ease-out' }}>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 4, letterSpacing: 2, textTransform: 'uppercase' }}>Next round in</div>
              <div style={{ fontSize: 48, fontWeight: 900, color: '#e2e8f0', lineHeight: 1 }}>{countdown}s</div>
            </div>
          )}
          {(isFlying || isCrashed) && (
            <div style={{ animation: 'fadeInUp 0.2s ease-out' }}>
              <div style={{
                fontSize: isCrashed ? 56 : 64,
                fontWeight: 900,
                lineHeight: 1,
                fontFamily: 'monospace',
                color: isCrashed ? '#ef4444' : multiplierColor(currentMultiplier),
                textShadow: isCrashed
                  ? '0 0 40px rgba(239,68,68,0.8)'
                  : `0 0 40px ${multiplierColor(currentMultiplier)}88`,
                transition: 'color 0.3s, text-shadow 0.3s',
                animation: isCrashed ? 'crashShake 0.4s ease-out' : undefined,
              }}>
                {currentMultiplier.toFixed(2)}x
              </div>
              {isCrashed && (
                <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444', marginTop: 8, letterSpacing: 2, animation: 'fadeInUp 0.3s 0.1s ease-out both' }}>
                  FLEW AWAY!
                </div>
              )}
            </div>
          )}
          {state.status === 'idle' && (
            <div style={{ fontSize: 14, color: '#334155', letterSpacing: 2 }}>WAITING…</div>
          )}
        </div>

        {/* My bet floating indicator */}
        {myBet && isFlying && !myBet.cashedOut && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)',
            borderRadius: 8, padding: '6px 12px', textAlign: 'right',
            animation: 'pulse 1s ease-in-out infinite',
          }}>
            <div style={{ fontSize: 10, color: '#22c55e', letterSpacing: 1 }}>YOUR BET</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>{fmtCoins(myBet.stake * currentMultiplier)}</div>
            <div style={{ fontSize: 10, color: '#475569' }}>{currentMultiplier.toFixed(2)}x</div>
          </div>
        )}

        {/* Cash out result */}
        {myBet?.cashedOut && myBet.payout && myBet.payout > 0 && (
          <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', borderRadius: 8, padding: '6px 12px', textAlign: 'right', animation: 'fadeInUp 0.3s ease-out' }}>
            <div style={{ fontSize: 10, color: '#22c55e', letterSpacing: 1 }}>CASHED OUT</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>+{fmtCoins(myBet.payout)}</div>
            <div style={{ fontSize: 10, color: '#475569' }}>{myBet.multiplier?.toFixed(2)}x</div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(34,197,94,0.9)', borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 600, color: 'white', animation: 'fadeInUp 0.2s ease-out', whiteSpace: 'nowrap' }}>
            {toast}
          </div>
        )}

        {/* Ground line */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #22c55e44, transparent)' }} />
      </div>

      {/* Bottom panel */}
      <div style={{ flexShrink: 0, background: '#131720', borderTop: '1px solid rgba(255,255,255,0.06)' }}>

        {/* Cash out button */}
        {isFlying && myBet && !myBet.cashedOut && (
          <button onClick={cashOut} style={{
            width: '100%', padding: '14px', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #16a34a, #15803d)',
            color: 'white', fontSize: 18, fontWeight: 900, letterSpacing: 1,
            animation: 'cashoutPulse 0.8s ease-in-out infinite',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <span>CASH OUT</span>
            <span style={{ fontSize: 22, fontWeight: 900 }}>{fmtCoins(myBet.stake * currentMultiplier)}</span>
            <span style={{ fontSize: 14 }}>({currentMultiplier.toFixed(2)}x)</span>
          </button>
        )}

        {/* Bet panel */}
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>

          {/* Stake + chips */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>Bet Amount</div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
              {CHIP_VALS.map((v, i) => (
                <button key={v} onClick={() => { setSelectedChip(v); setStake(s => s + v) }}
                  disabled={!isBetting || !!myBet}
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${CHIP_COLORS[i]}cc, ${CHIP_COLORS[i]}88)`,
                    border: selectedChip === v ? '2px solid white' : '2px solid rgba(255,255,255,0.2)',
                    color: 'white', fontSize: 10, fontWeight: 700, cursor: isBetting && !myBet ? 'pointer' : 'default',
                    opacity: isBetting && !myBet ? 1 : 0.4,
                  }}>
                  {v >= 1000 ? `${v/1000}k` : v}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, fontWeight: 700, color: '#c9a84c', minWidth: 80, textAlign: 'center', fontSize: 15 }}>
                {fmtCoins(stake)}
              </div>
              <button onClick={() => setStake(0)} disabled={!isBetting || !!myBet}
                style={{ padding: '6px 10px', background: 'rgba(220,38,38,0.2)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6, color: '#f87171', fontSize: 11, cursor: 'pointer', opacity: isBetting && !myBet ? 1 : 0.4 }}>
                CLR
              </button>
            </div>
          </div>

          {/* Auto cashout */}
          <div style={{ minWidth: 120 }}>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>Auto Cashout</div>
            <input
              type="number"
              value={autoCashout}
              onChange={e => setAutoCashout(e.target.value)}
              placeholder="e.g. 2.00"
              disabled={!isBetting || !!myBet}
              style={{
                width: '100%', padding: '8px 10px',
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6, color: '#e2e8f0', fontSize: 13,
                outline: 'none', marginBottom: 6,
                opacity: isBetting && !myBet ? 1 : 0.5,
              }}
            />
            <div style={{ fontSize: 9, color: '#334155' }}>Leave blank for manual</div>
          </div>

          {/* Place bet button */}
          <div>
            {isBetting && !myBet && (
              <button onClick={placeBet} disabled={stake === 0}
                style={{
                  padding: '10px 24px', border: 'none', borderRadius: 8, cursor: stake > 0 ? 'pointer' : 'default',
                  background: stake > 0 ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#1e293b',
                  color: 'white', fontSize: 14, fontWeight: 800, letterSpacing: 0.5,
                  opacity: stake > 0 ? 1 : 0.5,
                  boxShadow: stake > 0 ? '0 4px 20px rgba(34,197,94,0.3)' : 'none',
                }}>
                BET
              </button>
            )}
            {isBetting && myBet && (
              <div style={{ padding: '10px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22c55e', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>
                ✓ BET<br />{fmtCoins(myBet.stake)}
              </div>
            )}
            {!isBetting && !isFlying && (
              <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#334155', fontSize: 12, textAlign: 'center' }}>
                {isCrashed ? 'Crashed' : 'Wait…'}
              </div>
            )}
            {isFlying && !myBet && (
              <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#334155', fontSize: 12, textAlign: 'center' }}>
                In flight
              </div>
            )}
          </div>
        </div>

        {/* Player info */}
        <div style={{ padding: '4px 16px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#334155' }}>Player: {displayName}</span>
          {state.seedHash && <span style={{ fontSize: 9, color: '#1e293b', fontFamily: 'monospace' }}>Hash: {state.seedHash.slice(0,16)}…</span>}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp   { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes crashShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        @keyframes planeCrash { to { transform:translate(200px,-100px) rotate(30deg); opacity:0; } }
        @keyframes cashoutPulse { 0%,100%{box-shadow:0 0 20px rgba(34,197,94,0.4)} 50%{box-shadow:0 0 40px rgba(34,197,94,0.8)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
      `}</style>
    </div>
  )
}
